import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/api/endpoints';
import type { Match, Team } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { formatDateTime, isoToLocalInput, localInputToIso } from '@/lib/datetime';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

interface LoadedData {
  matches: Match[];
  teams: Team[];
}

export function AdminMatchesPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);

  const { data, loading, error, reload } = useAsync<LoadedData>(
    async (signal) => {
      const [matches, teams] = await Promise.all([
        adminApi.matchdayMatches(matchdayId, signal),
        adminApi.listTeams(signal),
      ]);
      return { matches, teams };
    },
    [matchdayId],
  );

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (loading) return <Spinner label="Spiele werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const teams = data?.teams ?? [];
  const matches = data?.matches ?? [];

  return (
    <>
      <p className="muted">
        <Link to="/admin/matchdays">← Spieltage</Link>
      </p>
      <h2>Spiele verwalten</h2>

      {matches.length === 0 && <Card>Noch keine Spiele in diesem Spieltag.</Card>}

      {matches.map((m) => (
        <MatchEditCard
          key={m.id}
          match={m}
          teams={teams}
          onChanged={reload}
          onTeamCreated={reload}
        />
      ))}

      <NewMatchCard
        matchdayId={matchdayId}
        teams={teams}
        onChanged={reload}
        onTeamCreated={reload}
      />
    </>
  );
}

/** Gemeinsames Editier-Formular für Anstoß + Heim/Auswärts mit Team-Dropdown / Neuanlage. */
interface MatchFormState {
  homeTeamId: number | '';
  awayTeamId: number | '';
  kickoff: string;
}

function TeamSelect({
  label,
  value,
  teams,
  onSelect,
  onTeamCreated,
}: {
  label: string;
  value: number | '';
  teams: Team[];
  onSelect: (id: number) => void;
  onTeamCreated: (team: Team) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShort, setNewShort] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createTeam() {
    setBusy(true);
    setError(null);
    try {
      const team = await adminApi.createTeam({
        name: newName.trim(),
        short_name: newShort.trim() || undefined,
      });
      onTeamCreated(team);
      onSelect(team.id);
      setAdding(false);
      setNewName('');
      setNewShort('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 160px' }}>
      <span className={styles.label}>{label}</span>
      {!adding ? (
        <>
          <select
            className={styles.input}
            value={value === '' ? '' : String(value)}
            onChange={(e) => {
              if (e.target.value === '__new__') {
                setAdding(true);
                return;
              }
              onSelect(Number(e.target.value));
            }}
          >
            <option value="">— Team wählen —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            <option value="__new__">+ Neuer Gegner …</option>
          </select>
        </>
      ) : (
        <div className={styles.newTeamBox}>
          <input
            className={styles.input}
            placeholder="Teamname"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder="Kürzel (optional)"
            value={newShort}
            onChange={(e) => setNewShort(e.target.value)}
          />
          <div className={styles.actions}>
            <Button
              className={styles.btnSm}
              disabled={busy || newName.trim() === ''}
              onClick={() => void createTeam()}
            >
              {busy ? '…' : 'Speichern'}
            </Button>
            <Button
              className={styles.btnSm}
              variant="ghost"
              type="button"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              Abbrechen
            </Button>
          </div>
          {error && <Alert>{error}</Alert>}
        </div>
      )}
    </label>
  );
}

function MatchEditCard({
  match,
  teams,
  onChanged,
  onTeamCreated,
}: {
  match: Match;
  teams: Team[];
  onChanged: () => void;
  onTeamCreated: () => void;
}) {
  const [form, setForm] = useState<MatchFormState>({
    homeTeamId: match.home_team.id,
    awayTeamId: match.away_team.id,
    kickoff: isoToLocalInput(match.kickoff_at),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    const iso = localInputToIso(form.kickoff);
    if (form.homeTeamId === '' || form.awayTeamId === '' || !iso) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.patchMatch(match.id, {
        home_team_id: Number(form.homeTeamId),
        away_team_id: Number(form.awayTeamId),
        kickoff_at: iso,
      });
      setSaved('Gespeichert.');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Spiel und zugehörige Tipps wirklich löschen?')) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.deleteMatch(match.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
      setBusy(false);
    }
  }

  const valid = form.homeTeamId !== '' && form.awayTeamId !== '' && form.kickoff !== '';

  return (
    <Card>
      <div className="muted" style={{ marginBottom: 8 }}>
        {match.home_team.name} – {match.away_team.name} · {formatDateTime(match.kickoff_at)}
      </div>
      <div className={styles.inlineForm}>
        <TeamSelect
          label="Heim"
          value={form.homeTeamId}
          teams={teams}
          onSelect={(id) => setForm((f) => ({ ...f, homeTeamId: id }))}
          onTeamCreated={onTeamCreated}
        />
        <TeamSelect
          label="Auswärts"
          value={form.awayTeamId}
          teams={teams}
          onSelect={(id) => setForm((f) => ({ ...f, awayTeamId: id }))}
          onTeamCreated={onTeamCreated}
        />
        <label className={styles.field} style={{ marginBottom: 0 }}>
          <span className={styles.label}>Anstoß</span>
          <input
            type="datetime-local"
            className={styles.deadlineInput}
            value={form.kickoff}
            onChange={(e) => setForm((f) => ({ ...f, kickoff: e.target.value }))}
          />
        </label>
        <div className={styles.actions}>
          <Button className={styles.btnSm} disabled={busy || !valid} onClick={() => void save()}>
            {busy ? '…' : 'Speichern'}
          </Button>
          <Button
            className={`${styles.btnSm} ${styles.btnDanger}`}
            disabled={busy}
            onClick={() => void remove()}
          >
            Löschen
          </Button>
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

function NewMatchCard({
  matchdayId,
  teams,
  onChanged,
  onTeamCreated,
}: {
  matchdayId: number;
  teams: Team[];
  onChanged: () => void;
  onTeamCreated: () => void;
}) {
  const [form, setForm] = useState<MatchFormState>({
    homeTeamId: '',
    awayTeamId: '',
    kickoff: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function create() {
    const iso = localInputToIso(form.kickoff);
    if (form.homeTeamId === '' || form.awayTeamId === '' || !iso) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.createMatch({
        matchday_id: matchdayId,
        home_team_id: Number(form.homeTeamId),
        away_team_id: Number(form.awayTeamId),
        kickoff_at: iso,
      });
      setSaved('Spiel angelegt.');
      setForm({ homeTeamId: '', awayTeamId: '', kickoff: '' });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  const valid = form.homeTeamId !== '' && form.awayTeamId !== '' && form.kickoff !== '';

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Spiel hinzufügen</h3>
      <div className={styles.inlineForm}>
        <TeamSelect
          label="Heim"
          value={form.homeTeamId}
          teams={teams}
          onSelect={(id) => setForm((f) => ({ ...f, homeTeamId: id }))}
          onTeamCreated={onTeamCreated}
        />
        <TeamSelect
          label="Auswärts"
          value={form.awayTeamId}
          teams={teams}
          onSelect={(id) => setForm((f) => ({ ...f, awayTeamId: id }))}
          onTeamCreated={onTeamCreated}
        />
        <label className={styles.field} style={{ marginBottom: 0 }}>
          <span className={styles.label}>Anstoß</span>
          <input
            type="datetime-local"
            className={styles.deadlineInput}
            value={form.kickoff}
            onChange={(e) => setForm((f) => ({ ...f, kickoff: e.target.value }))}
          />
        </label>
        <Button className={styles.btnSm} disabled={busy || !valid} onClick={() => void create()}>
          {busy ? '…' : 'Hinzufügen'}
        </Button>
      </div>
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}
